import React, { useState, useEffect, useRef } from 'react'
import { Loader } from '@googlemaps/js-api-loader'

const MapView = ({ locations, onAddLocation, loading }) => {
  const mapRef = useRef(null)
  const [map, setMap] = useState(null)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newLocation, setNewLocation] = useState({
    name: '',
    address: '',
    lat: 0,
    lng: 0,
    notes: ''
  })

  useEffect(() => {
    const initMap = async () => {
      const loader = new Loader({
        apiKey: 'YOUR_GOOGLE_MAPS_API_KEY', // TODO: Replace with actual API key
        version: 'weekly',
      })

      try {
        await loader.load()
        
        const mapOptions = {
          center: { lat: 37.7749, lng: -122.4194 }, // Default to San Francisco
          zoom: 12,
          mapTypeId: 'roadmap',
        }

        const googleMap = new google.maps.Map(mapRef.current, mapOptions)
        setMap(googleMap)

        // Add click listener to map for adding new locations
        googleMap.addListener('click', (event) => {
          const lat = event.latLng.lat()
          const lng = event.latLng.lng()
          
          // Get address from coordinates
          const geocoder = new google.maps.Geocoder()
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
              setNewLocation({
                ...newLocation,
                lat,
                lng,
                address: results[0].formatted_address
              })
              setShowAddForm(true)
            }
          })
        })

      } catch (error) {
        console.error('Error loading Google Maps:', error)
      }
    }

    if (mapRef.current && !map) {
      initMap()
    }
  }, [map])

  // Add markers for existing locations
  useEffect(() => {
    if (map && locations.length > 0) {
      locations.forEach(location => {
        const marker = new google.maps.Marker({
          position: { lat: location.lat, lng: location.lng },
          map: map,
          title: location.name,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="15" fill="#ff6b35" stroke="#fff" stroke-width="3"/>
                <text x="20" y="26" text-anchor="middle" fill="white" font-size="16">🚚</text>
              </svg>
            `),
            scaledSize: new google.maps.Size(40, 40)
          }
        })

        marker.addListener('click', () => {
          setSelectedLocation(location)
        })
      })
    }
  }, [map, locations])

  const handleAddLocation = async () => {
    if (!newLocation.name || !newLocation.lat || !newLocation.lng) return

    await onAddLocation({
      name: newLocation.name,
      address: newLocation.address,
      lat: newLocation.lat,
      lng: newLocation.lng,
      notes: newLocation.notes,
      performance: {
        sales: 0,
        customers: 0,
        rating: 0
      }
    })

    setNewLocation({ name: '', address: '', lat: 0, lng: 0, notes: '' })
    setShowAddForm(false)
  }

  return (
    <div className="map-view">
      <div className="map-container">
        <div ref={mapRef} className="google-map" />
      </div>

      <div className="map-sidebar">
        <div className="locations-list">
          <h3>Your Locations ({locations.length})</h3>
          {locations.map(location => (
            <div 
              key={location.id} 
              className="location-item"
              onClick={() => setSelectedLocation(location)}
            >
              <div className="location-name">{location.name}</div>
              <div className="location-address">{location.address}</div>
              {location.performance && (
                <div className="location-stats">
                  💰 ${location.performance.sales} | 👥 {location.performance.customers}
                </div>
              )}
            </div>
          ))}
          
          {locations.length === 0 && (
            <div className="empty-state">
              <p>🗺️ Click on the map to add your first location!</p>
            </div>
          )}
        </div>

        {selectedLocation && (
          <div className="location-details">
            <h3>{selectedLocation.name}</h3>
            <p>{selectedLocation.address}</p>
            {selectedLocation.notes && <p><strong>Notes:</strong> {selectedLocation.notes}</p>}
            <button onClick={() => setSelectedLocation(null)}>Close</button>
          </div>
        )}
      </div>

      {showAddForm && (
        <div className="add-location-modal">
          <div className="modal-content">
            <h3>Add New Location</h3>
            <form onSubmit={(e) => { e.preventDefault(); handleAddLocation(); }}>
              <input
                type="text"
                placeholder="Location name (e.g., 'Downtown Corner')"
                value={newLocation.name}
                onChange={(e) => setNewLocation({...newLocation, name: e.target.value})}
                required
              />
              <input
                type="text"
                value={newLocation.address}
                placeholder="Address"
                readOnly
              />
              <textarea
                placeholder="Notes (optional)"
                value={newLocation.notes}
                onChange={(e) => setNewLocation({...newLocation, notes: e.target.value})}
              />
              <div className="modal-actions">
                <button type="submit" disabled={loading}>
                  {loading ? 'Adding...' : 'Add Location'}
                </button>
                <button type="button" onClick={() => setShowAddForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default MapView